import React from "react";
import moment from "moment";
import IDocument from "../../models/IDocument";
import ResponsiveTableCell from "../ResponsiveTableCell";
import {
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Typography,
    TableSortLabel,
    makeStyles,
    Theme,
    Tooltip,
    ButtonGroup
} from "@material-ui/core";
import Loading from "../shared/Loading";
import TableStyle from "../shared/Styling/TableStyling";
import ProjectDocumentSource from "../../models/ProjectDocumentSource";
import { Edit } from "@material-ui/icons";

import DocumentsDeleteButton from "./DocumentsDeleteButton";
import DocumentDownloadButton from "./DocumentDownloadButton";
import { ConfirmationServiceProvider } from "../../services/ConfirmationService";
import ICategory from "../../models/ICategory";
import { DateDisplay } from "../shared/DateDisplay";

const styleOverrides = makeStyles((theme: Theme) => ({
    headerTitle: {
        fontSize: "1.3rem",
        padding: theme.spacing(4, 4, 4, 4),
        color: theme.palette.primary.contrastText
    },
    headerUploadedOn: {
        fontSize: "1.3rem",
        padding: theme.spacing(4, 4, 4, 2),
        color: theme.palette.primary.contrastText,
        minWidth: "150px"
    }
}));

interface IDocumentsTable {
    documents: IDocument[] | null;
    isLoading: boolean;
    isProcessing: boolean;
    categories: ICategory[] | null;
    onRemoveDocument(document: IDocument): void;
    onDownloadRequest(document: IDocument): void;
    onEdit(document: IDocument): void;
}

const DocumentsTable: React.FC<IDocumentsTable> = ({
    documents,
    isLoading,
    onRemoveDocument,
    onDownloadRequest,
    onEdit,
    categories
}) => {
    const classes = TableStyle();
    const classOverrides = styleOverrides();

    type Order = "asc" | "desc";

    const [order, setOrder] = React.useState<Order>("asc");
    const [orderBy, setOrderBy] = React.useState("title");
    const [sortedDocuments, setSortedDocuments] = React.useState<
        IDocument[] | null
    >(documents);

    const stringSort = (a: string | null, b: string | null, order: Order) => {
        return order === "asc"
            ? (a === null ? "" : a.toLowerCase()) <
              (b === null ? "" : b.toLowerCase())
                ? 1
                : (a === null ? "" : a.toLowerCase()) >
                  (b === null ? "" : b.toLowerCase())
                ? -1
                : 0
            : (b === null ? "" : b.toLowerCase()) <
              (a === null ? "" : a.toLowerCase())
            ? 1
            : (b === null ? "" : b.toLowerCase()) >
              (a === null ? "" : a.toLowerCase())
            ? -1
            : 0;
    };

    const sortHandler = orderBy => {
        const isDesc = order === "desc";
        setOrder(isDesc ? "asc" : "desc");
        setOrderBy(orderBy);

        if (documents !== null) {
            switch (orderBy) {
                case "title":
                    documents.sort((a, b) =>
                        stringSort(a.title, b.title, order)
                    );
                    break;
                case "description":
                    documents.sort((a, b) =>
                        stringSort(a.description, b.description, order)
                    );
                    break;
                case "category":
                    documents.sort((a, b) =>
                        order === "asc" ? a.type - b.type : b.type - a.type
                    );
                    break;
                case "source":
                    documents.sort((a, b) =>
                        order === "asc"
                            ? a.source - b.source
                            : b.source - a.source
                    );
                    break;
                case "uploadedBy":
                    documents.sort((a, b) =>
                        stringSort(a.uploadedBy, b.uploadedBy, order)
                    );
                    break;
                case "uploadedOn":
                    documents.sort((a, b) =>
                        order === "asc"
                            ? moment(a.uploadedOn).isBefore(b.uploadedOn)
                                ? 1
                                : moment(a.uploadedOn).isAfter(b.uploadedOn)
                                ? -1
                                : 0
                            : moment(a.uploadedOn).isAfter(b.uploadedOn)
                            ? 1
                            : moment(a.uploadedOn).isBefore(b.uploadedOn)
                            ? -1
                            : 0
                    );
                    break;
            }
            setSortedDocuments(documents.map(x => x));
        }
    };

    React.useEffect(() => {
        if (documents !== null) {
            setSortedDocuments(documents.map(x => x));
        }
    }, [documents]);

    const GetCategory = document => {
        let category =
            categories != null
                ? categories.find(x => x.id === document.type)
                : null;

        if (category != null) {
            return category.name;
        } else {
            return "Unknown";
        }
    };

    const GetDescription = document => {
        let maxLength = 65;
        let description = document.description;

        if (document.description != null) {
            if (description.length <= maxLength) {
                return description;
            } else {
                return description.slice(0, maxLength) + "...";
            }
        }
    };

    return (
        <ConfirmationServiceProvider>
            <Table className={classes.table}>
                <TableHead className={classes.header}>
                    <TableRow>
                        <TableCell className={classOverrides.headerTitle}>
                            <TableSortLabel
                                active={orderBy === "title"}
                                direction={order}
                                onClick={() => sortHandler("title")}
                            >
                                Title
                            </TableSortLabel>
                        </TableCell>
                        <ResponsiveTableCell
                            className={classes.headerCell}
                            hiddenWhen="xs"
                        >
                            <TableSortLabel
                                active={orderBy === "description"}
                                direction={order}
                                onClick={() => sortHandler("description")}
                            >
                                Description
                            </TableSortLabel>
                        </ResponsiveTableCell>
                        <ResponsiveTableCell
                            className={classes.headerCell}
                            hiddenWhen="xs"
                        >
                            <TableSortLabel
                                active={orderBy === "category"}
                                direction={order}
                                onClick={() => sortHandler("category")}
                            >
                                Category
                            </TableSortLabel>
                        </ResponsiveTableCell>
                        <ResponsiveTableCell
                            className={classes.headerCell}
                            hiddenWhen="sm"
                        >
                            <TableSortLabel
                                active={orderBy === "source"}
                                direction={order}
                                onClick={() => sortHandler("source")}
                            >
                                Source
                            </TableSortLabel>
                        </ResponsiveTableCell>
                        <ResponsiveTableCell
                            className={classes.headerCell}
                            hiddenWhen="md"
                        >
                            <TableSortLabel
                                active={orderBy === "uploadedBy"}
                                direction={order}
                                onClick={() => sortHandler("uploadedBy")}
                            >
                                Uploaded By
                            </TableSortLabel>
                        </ResponsiveTableCell>
                        <ResponsiveTableCell
                            className={classes.headerCell}
                            hiddenWhen="sm"
                        >
                            <TableSortLabel
                                active={orderBy === "uploadedOn"}
                                direction={order}
                                onClick={() => sortHandler("uploadedOn")}
                            >
                                Uploaded On
                            </TableSortLabel>
                        </ResponsiveTableCell>
                        <TableCell className={classes.headerCell}></TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {isLoading ? (
                        <TableRow className={classes.row}>
                            <TableCell
                                colSpan={7}
                                style={{
                                    position: "absolute",
                                    left: "48%",
                                    top: "600px",
                                    minHeight: "600px"
                                }}
                            >
                                <Loading>
                                    <CircularProgress />
                                </Loading>
                                <Typography
                                    variant="subtitle1"
                                    style={{
                                        position: "absolute",
                                        left: "-15px",
                                        top: "60px"
                                    }}
                                >
                                    Loading Documents...
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : sortedDocuments === null ||
                      sortedDocuments.length === 0 ? (
                        <TableRow className={classes.row}>
                            <TableCell colSpan={7}>
                                <Typography className={classes.message}>
                                    <cite>
                                        There are currently no documents
                                        associated with this project. Please use
                                        the upload button to add some documents.
                                    </cite>
                                </Typography>
                            </TableCell>
                        </TableRow>
                    ) : (
                        sortedDocuments.map(document => (
                            <TableRow key={document.id} className={classes.row}>
                                <TableCell>{document.title}</TableCell>

                                <ResponsiveTableCell hiddenWhen="xs">
                                    <Tooltip title={document.description ?? ""}>
                                        <span>{GetDescription(document)}</span>
                                    </Tooltip>
                                </ResponsiveTableCell>

                                <ResponsiveTableCell hiddenWhen="xs">
                                    {GetCategory(document)}
                                </ResponsiveTableCell>
                                <ResponsiveTableCell hiddenWhen="sm">
                                    {ProjectDocumentSource[document.source]}
                                </ResponsiveTableCell>
                                <ResponsiveTableCell hiddenWhen="md">
                                    {document.uploadedBy}
                                </ResponsiveTableCell>
                                <ResponsiveTableCell hiddenWhen="sm">
                                    <DateDisplay date={document.uploadedOn} />
                                </ResponsiveTableCell>
                                <ResponsiveTableCell
                                    align="right"
                                    className={classes.tableCellIcon}
                                >
                                    <ButtonGroup>
                                        {document.isDeletePermitted && (
                                            <DocumentsDeleteButton
                                                document={document}
                                                onRemoveDocument={() =>
                                                    onRemoveDocument(document)
                                                }
                                            />
                                        )}

                                        <DocumentDownloadButton
                                            onDocumentDownload={async () =>
                                                await onDownloadRequest(
                                                    document
                                                )
                                            }
                                        />

                                        {document.isUpdatePermitted && (
                                            <Edit
                                                onClick={() => onEdit(document)}
                                                className={`${classes.button} ${classes.icon}`}
                                            />
                                        )}
                                    </ButtonGroup>
                                </ResponsiveTableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </ConfirmationServiceProvider>
    );
};

export default DocumentsTable;
